import { toast } from 'react-toastify';

class VersionChecker {
    constructor() {
        this.currentVersion = null;
        this.checkInterval = null;
        this.isChecking = false;
        this.hasShownUpdateNotification = false;
        
        // Get initial version from package.json or build time
        this.initializeVersion();
    }

    initializeVersion() {
        // Try to get version from meta tag first (can be set during build)
        const metaVersion = document.querySelector('meta[name="app-version"]');
        if (metaVersion) {
            this.currentVersion = metaVersion.getAttribute('content');
            return;
        }

        // Fallback to a simple hash of the main JS files
        this.generateVersionFromAssets();
    }

    generateVersionFromAssets() {
        try {
            // Get all script tags and generate a simple hash
            const scripts = Array.from(document.querySelectorAll('script[src]'))
                .map(script => script.src)
                .filter(src => src.includes('.js'))
                .join('');
            
            // Simple hash function
            let hash = 0;
            for (let i = 0; i < scripts.length; i++) {
                const char = scripts.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash; // Convert to 32-bit integer
            }
            
            this.currentVersion = Math.abs(hash).toString(16);
        } catch (error) {
            console.warn('Could not generate version hash:', error);
            this.currentVersion = Date.now().toString(); // Fallback to timestamp
        }
    }

    async checkForUpdates() {
        if (this.isChecking) return;
        
        this.isChecking = true;
        
        try {
            // Method 1: Check if main.js returns a different hash/etag
            const response = await fetch(window.location.origin + window.location.pathname, {
                method: 'HEAD',
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                }
            });

            const etag = response.headers.get('etag');
            const lastModified = response.headers.get('last-modified');
            
            // Create a version identifier from response headers
            const serverVersion = etag || lastModified || 'unknown';
            
            // If we have a stored version and it's different, there might be an update
            const storedVersion = localStorage.getItem('app-version');
            
            if (storedVersion && storedVersion !== serverVersion) {
                console.log('Version mismatch detected:', storedVersion, 'vs', serverVersion);
                this.handleVersionMismatch();
            } else if (!storedVersion) {
                // First time, store the version
                localStorage.setItem('app-version', serverVersion);
            }

        } catch (error) {
            console.warn('Version check failed:', error);
            // Try alternative method: check for chunk loading errors
            this.checkForChunkErrors();
        } finally {
            this.isChecking = false;
        }
    }

    checkForChunkErrors() {
        // Listen for chunk loading errors
        const originalConsoleError = console.error;
        console.error = (...args) => {
            const errorMessage = args.join(' ').toLowerCase();
            
            if (errorMessage.includes('loading chunk') || 
                errorMessage.includes('loading css chunk') ||
                errorMessage.includes('chunkloaderror')) {
                console.log('Chunk loading error detected, possible deployment update');
                this.handleVersionMismatch();
            }
            
            originalConsoleError.apply(console, args);
        };
    }

    handleVersionMismatch() {
        if (this.hasShownUpdateNotification) return;
        
        this.hasShownUpdateNotification = true;
        
        // Show update notification
        toast.info('New version available. Updating application...', {
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            onClose: () => this.reloadApplication()
        });
        
        // Auto reload after toast
        setTimeout(() => this.reloadApplication(), 2500);
    }

    reloadApplication() {
        // Clear all caches
        if ('caches' in window) {
            caches.keys().then(names => {
                names.forEach(name => {
                    caches.delete(name);
                });
            });
        }

        // Clear localStorage version to force fresh check
        localStorage.removeItem('app-version');
        
        // Clear any other cached data that might cause issues
        sessionStorage.clear();
        
        // Force reload with cache bypass
        window.location.reload(true);
    }

    startPeriodicCheck(intervalMinutes = 5) {
        // Check every 5 minutes by default
        this.checkInterval = setInterval(() => {
            this.checkForUpdates();
        }, intervalMinutes * 60 * 1000);
        
        // Also check immediately
        setTimeout(() => this.checkForUpdates(), 1000);
    }

    stopPeriodicCheck() {
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }
    }

    // Method to manually trigger update check
    forceCheck() {
        return this.checkForUpdates();
    }
}

// Create singleton instance
const versionChecker = new VersionChecker();

export default versionChecker;
